import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
const defaultPreferences = {
    cushion_v1: 'V1',
    cushion_v2: 'V2',
    cushion_v3: 'V3',
    cushion_v4: 'V4',
    divisor_d1: 'D1',
};
const SectionColumnPreferencesContext = createContext(undefined);
export const useSectionColumnPreferences = () => {
    const context = useContext(SectionColumnPreferencesContext);
    if (!context) {
        throw new Error('useSectionColumnPreferences must be used within SectionColumnPreferencesProvider');
    }
    return context;
};
export const SectionColumnPreferencesProvider = ({ children }) => {
    const { isAuthenticated, isLoading: authLoading } = useAuth();
    const [preferences, setPreferences] = useState(defaultPreferences);
    const [isLoading, setIsLoading] = useState(true);
    const refreshPreferences = async () => {
        if (!isAuthenticated || authLoading) {
            setPreferences(defaultPreferences);
            setIsLoading(false);
            return;
        }
        try {
            setIsLoading(true);
            const response = await fetch('/api/section-column-aliases', {
                credentials: 'include',
            });
            if (response.ok) {
                const data = await response.json();
                setPreferences(data.preferences || defaultPreferences);
            }
            else {
                setPreferences(defaultPreferences);
            }
        }
        catch (error) {
            console.error('Error fetching section column preferences:', error);
            setPreferences(defaultPreferences);
        }
        finally {
            setIsLoading(false);
        }
    };
    const updatePreferences = async (newPreferences) => {
        const updatedPreferences = { ...preferences, ...newPreferences };
        try {
            const response = await fetch('/api/section-column-aliases', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({ preferences: updatedPreferences }),
            });
            if (response.ok) {
                setPreferences(updatedPreferences);
            }
            else {
                throw new Error('Failed to save preferences');
            }
        }
        catch (error) {
            console.error('Error updating section column preferences:', error);
            throw error;
        }
    };
    useEffect(() => {
        if (isAuthenticated && !authLoading) {
            refreshPreferences();
        }
        else if (!authLoading) {
            setPreferences(defaultPreferences);
            setIsLoading(false);
        }
    }, [isAuthenticated, authLoading]);
    return (_jsx(SectionColumnPreferencesContext.Provider, { value: {
            preferences,
            isLoading,
            updatePreferences,
            refreshPreferences,
        }, children: children }));
};
